import { createSlice } from "@reduxjs/toolkit";
import { resetAll } from "./baseSlice";
import intl from "../../i18n/intl";
import { toast } from "react-toastify";

const initialState = {
  data: [],
  record: null,
  requesting: false,
  error: null,
  action: null,
  users: {},
  query: {
    pageNo: 1,
    pageSize: 50,
    sortBy: "asc",
    sortColumn: "name",
    namePattern: "",
  },
};

const customIntl = intl();

const audienceSlice = createSlice({
  name: "audiences",
  initialState,
  extraReducers: (builder) => builder.addCase(resetAll, () => initialState),
  reducers: {
    listRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "list";
      state.record = null;
    },
    listSuccess: (state, action) => {
      state.data = action.payload.response;
      state.requesting = false;
      state.error = null;
    },
    listFail: (state, action) => {
      state.data = [];
      state.requesting = false;
      state.error = action.payload;
    },
    setQuery: (state, action) => {
      state.query = {
        ...state.query,
        ...action.payload,
      };
    },
    createRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "create";
    },
    createSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage({ id: "audience_create_success" })
      );
    },
    createFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    detailsRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "details";
      state.record = null;
    },
    detailsSuccess: (state, action) => {
      state.record = {
        ...state.record,
        ...action.payload.response,
      };
      state.requesting = false;
      state.error = null;
    },
    detailsFail: (state, action) => {
      state.record = null;
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    updateDetailRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "update";
    },
    updateDetailSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage(
          { id: "action_success" },
          {
            action: customIntl.formatMessage({ id: "update_performed" }),
            object: customIntl.formatMessage({ id: "audience" }),
          }
        )
      );
    },
    updateDetailFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    audienceUserListRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "userList";
    },
    audienceUserListSuccess: (state, action) => {
      state.users = action.payload.response;
      state.requesting = false;
      state.error = null;
    },
    audienceUserListFail: (state, action) => {
      state.users = [];
      state.requesting = false;
      state.error = action.payload;
    },
    audienceAssignmentistRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "assignmentList";
    },
    audienceAssignmentistSuccess: (state, action) => {
      state.record = {
        ...state.record,
        assignments: action.payload.response,
      };
      state.requesting = false;
      state.error = null;
    },
    audienceAssignmentistFail: (state, action) => {
      state.record = {
        ...state.record,
        assignments: [],
      };
      state.requesting = false;
      state.error = action.payload;
    },
    addAssignmentRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "addAssignment";
    },
    addAssignmentSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage({ id: "assignment_create_success" })
      );
    },
    addAssignmentFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    deleteRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "delete";
    },
    deleteSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage(
          { id: "action_success" },
          {
            action: customIntl.formatMessage({ id: "delete_performed" }),
            object: customIntl.formatMessage({ id: "audience" }),
          }
        )
      );
    },
    deleteFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    deleteAssignmentRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "deleteAssignment";
    },
    deleteAssignmentSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage({ id: "assignment_delete_success" })
      );
    },
    deleteAssignmentFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    exportUsersRequest: (state, action) => {},
    exportUsersSuccess: (state, action) => {},
    exportUsersFail: (state, action) => {},
  },
});

export const {
  createFail,
  createRequest,
  createSuccess,
  listFail,
  listRequest,
  listSuccess,
  detailsFail,
  detailsRequest,
  detailsSuccess,
  setQuery,
  updateDetailFail,
  updateDetailRequest,
  updateDetailSuccess,
  audienceUserListFail,
  audienceUserListRequest,
  audienceUserListSuccess,
  audienceAssignmentistFail,
  audienceAssignmentistRequest,
  audienceAssignmentistSuccess,
  addAssignmentFail,
  addAssignmentRequest,
  addAssignmentSuccess,
  deleteFail,
  deleteRequest,
  deleteSuccess,
  deleteAssignmentFail,
  deleteAssignmentRequest,
  deleteAssignmentSuccess,
  exportUsersFail,
  exportUsersRequest,
  exportUsersSuccess,
} = audienceSlice.actions;
export default audienceSlice.reducer;
